import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { requestJson } from '@/lib/api/client/request'
import type { ContractBodyInput } from '@/lib/api/contracts'
import {
  createCredentialDraftContract,
  createWorkspaceCredentialContract,
  deleteWorkspaceCredentialContract,
  getWorkspaceCredentialContract,
  updateWorkspaceCredentialContract,
  type WorkspaceCredential,
  type WorkspaceCredentialRole,
  type WorkspaceCredentialType,
} from '@/lib/api/contracts'
import { type ResourceOwner, resourceScopeFromOwner } from '@/lib/core/resource-scope'
import { environmentKeys } from '@/hooks/queries/environment'
import { oauthConnectionsKeys } from '@/hooks/queries/oauth/oauth-connections'
import { personalCredentialKeys } from '@/hooks/queries/personal-credentials'
import { workspaceCredentialKeys } from '@/hooks/queries/utils/credential-keys'
import { workspaceCredentialListQueryOptions } from '@/hooks/queries/utils/fetch-workspace-credentials'
import { invalidateSelectorQueries } from '@/hooks/queries/utils/selector-keys'

/**
 * Key prefix for OAuth credential queries.
 * Duplicated here to avoid circular imports with oauth-credentials.ts.
 */
const OAUTH_CREDENTIALS_KEY = ['oauthCredentials'] as const

export const WORKSPACE_CREDENTIAL_DETAIL_STALE_TIME = 60 * 1000

export type { WorkspaceCredential, WorkspaceCredentialRole, WorkspaceCredentialType }

export function useWorkspaceCredentials(params: {
  workspaceId?: string
  type?: WorkspaceCredentialType
  providerId?: string
  enabled?: boolean
}) {
  const { workspaceId, type, providerId, enabled = true } = params

  return useQuery({
    ...workspaceCredentialListQueryOptions(workspaceId, type, providerId),
    enabled: Boolean(workspaceId) && enabled,
  })
}

export function useWorkspaceCredential(
  credentialId?: string,
  enabled = true,
  workspaceId?: string
) {
  return useQuery<WorkspaceCredential | null>({
    queryKey: workspaceCredentialKeys.detailForWorkspace(credentialId, workspaceId),
    queryFn: async ({ signal }) => {
      if (!credentialId) return null
      const data = await requestJson(getWorkspaceCredentialContract, {
        params: { id: credentialId },
        query: { workspaceId },
        signal,
      })
      return data.credential ?? null
    },
    enabled: Boolean(credentialId) && enabled,
    staleTime: WORKSPACE_CREDENTIAL_DETAIL_STALE_TIME,
    // The credential-detail form seeds editable name/description fields from
    // this data, so a background focus refetch during an edit could clobber
    // an unsaved draft. Off the desktop focus-refetch default; no-op on web.
    refetchOnWindowFocus: false,
  })
}

/**
 * The workspace a credential is written to. A credential belongs to one brand — a second
 * brand in the same org must not see it (`server/agents/src/credentials`) — so an
 * organization owner has nowhere to go, and refusing it here beats posting to a route
 * nothing answers.
 */
export function credentialWorkspaceId(owner: ResourceOwner): string {
  const scope = resourceScopeFromOwner(owner)
  if (scope.kind !== 'workspace') throw new Error('A credential belongs to a brand')
  return scope.workspaceId
}

export function useCreateCredentialDraft() {
  return useMutation({
    mutationFn: async ({
      workspaceId,
      organizationId,
      ...body
    }: Omit<ContractBodyInput<typeof createCredentialDraftContract>, 'workspaceId'> &
      ResourceOwner) =>
      requestJson(createCredentialDraftContract, {
        body: { ...body, workspaceId: credentialWorkspaceId({ workspaceId, organizationId }) },
      }),
  })
}

export function useCreateWorkspaceCredential() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload: ContractBodyInput<typeof createWorkspaceCredentialContract>) => {
      return requestJson(createWorkspaceCredentialContract, { body: payload })
    },
    onSettled: () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: personalCredentialKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: workspaceCredentialKeys.lists(),
        }),
        queryClient.invalidateQueries({
          queryKey: OAUTH_CREDENTIALS_KEY,
        }),
        invalidateSelectorQueries(queryClient),
      ]),
  })
}

export function useUpdateWorkspaceCredential(workspaceId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      payload: {
        credentialId: string
      } & ContractBodyInput<typeof updateWorkspaceCredentialContract>
    ) => {
      // Forward the whole contract body rather than re-listing its fields: a
      // hand-maintained allowlist silently drops any field added to the
      // contract later, and the payload type makes that invisible to `tsc`.
      const { credentialId, ...body } = payload
      return requestJson(updateWorkspaceCredentialContract, {
        params: { id: credentialId },
        body,
        query: { workspaceId },
      })
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: workspaceCredentialKeys.detail(variables.credentialId),
      })
      await queryClient.cancelQueries({ queryKey: workspaceCredentialKeys.lists() })

      const previousLists = queryClient.getQueriesData<WorkspaceCredential[]>({
        queryKey: workspaceCredentialKeys.lists(),
      })
      const previousDetail = queryClient.getQueryData<WorkspaceCredential | null>(
        workspaceCredentialKeys.detailForWorkspace(variables.credentialId, workspaceId)
      )

      /** Applies the in-flight edit to one cached credential. */
      const withEdit = (cred: WorkspaceCredential): WorkspaceCredential => ({
        ...cred,
        ...(variables.displayName !== undefined ? { displayName: variables.displayName } : {}),
        ...(variables.description !== undefined
          ? { description: variables.description ?? null }
          : {}),
        ...(variables.unredacted !== undefined ? { unredacted: variables.unredacted } : {}),
      })

      /*
       * The detail cache is patched alongside the lists, not just cancelled: a
       * detail-backed editor compares its drafts against this entry to decide
       * whether it is dirty, so leaving it stale keeps the surface dirty after a
       * successful save until the `onSettled` refetch lands — long enough for
       * Discard to restore the pre-save value over the committed one.
       */
      queryClient.setQueryData<WorkspaceCredential | null>(
        workspaceCredentialKeys.detailForWorkspace(variables.credentialId, workspaceId),
        (old) => (old ? withEdit(old) : old)
      )

      queryClient.setQueriesData<WorkspaceCredential[]>(
        { queryKey: workspaceCredentialKeys.lists() },
        (old) => {
          if (!old) return old
          return old.map((cred) => (cred.id === variables.credentialId ? withEdit(cred) : cred))
        }
      )

      return { previousLists, previousDetail }
    },
    onError: (_err, variables, context) => {
      if (context?.previousLists) {
        for (const [queryKey, data] of context.previousLists) {
          queryClient.setQueryData(queryKey, data)
        }
      }
      if (context?.previousDetail !== undefined) {
        queryClient.setQueryData(
          workspaceCredentialKeys.detailForWorkspace(variables.credentialId, workspaceId),
          context.previousDetail
        )
      }
    },
    onSettled: (_data, _error, variables) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: personalCredentialKeys.lists() }),
        queryClient.invalidateQueries({
          queryKey: workspaceCredentialKeys.detail(variables.credentialId),
        }),
        queryClient.invalidateQueries({
          queryKey: workspaceCredentialKeys.lists(),
        }),
        queryClient.invalidateQueries({
          queryKey: OAUTH_CREDENTIALS_KEY,
        }),
        invalidateSelectorQueries(queryClient),
      ]),
  })
}

export function useDeleteWorkspaceCredential(workspaceId?: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentialId: string) => {
      return requestJson(deleteWorkspaceCredentialContract, {
        params: { id: credentialId },
        query: { workspaceId },
      })
    },
    onSettled: (_data, _error, credentialId) =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: personalCredentialKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: workspaceCredentialKeys.detail(credentialId) }),
        queryClient.invalidateQueries({ queryKey: workspaceCredentialKeys.lists() }),
        queryClient.invalidateQueries({ queryKey: OAUTH_CREDENTIALS_KEY }),
        queryClient.invalidateQueries({ queryKey: environmentKeys.all }),
        queryClient.invalidateQueries({ queryKey: oauthConnectionsKeys.connections() }),
        invalidateSelectorQueries(queryClient),
      ]),
  })
}
